import { z } from "zod";
/**
 * OMS: the outreach team's inventory of sites that sell a guest post.
 *
 * Two boundaries live here. The vendor's rows, as its webhook returns them, and
 * the shape our own API hands the Off-page screen.
 *
 * Every figure is OMS's own, passed through as it came (agreed 2026-10-04). A
 * domain authority of 559 is shown as 559: the screen says the number is from
 * OMS rather than correcting it into one that looks like ours.
 */
/**
 * A count OMS may send as a number or as digits in a string — the Semrush
 * columns arrive as `"107119791"` (probed 2026-10-05). An empty string is OMS
 * saying nothing; anything that is not digits is a changed shape and fails the
 * parse, loudly, rather than becoming a guess.
 */
const omsCount = z
    .union([z.number(), z.string().regex(/^(\d+(\.\d+)?)?$/u, "digits or empty")])
    .nullable()
    .transform((value) => (value === "" || value === null ? null : Number(value)));
/**
 * One row of the OMS webhook.
 *
 * Every figure is nullable because OMS leaves them empty — a fifth of rows have
 * no domain rating and no price. Absent stays absent; it is never read as 0.
 * Probed against the live webhook on 2026-10-04 (docs/research-log.md).
 */
export const omsSiteRowSchema = z.object({
    id: z.number().int(),
    /** Always `www.` and lowercase, matched exactly. */
    website: z.string().min(1),
    niche: z.string().nullable(),
    domainAuthority: z.number().nullable(),
    pageAuthority: z.number().nullable(),
    domainRating: z.number().nullable(),
    spamScore: z.number().nullable(),
    /** US dollars. 0 is not a price we can quote, so it is treated as none. */
    sellingPrice: z.number().nullable(),
    /** Semrush's monthly traffic estimates, as OMS holds them (found 2026-10-05). */
    semrushTraffic: omsCount,
    semrushOrganicTraffic: omsCount,
    updatedAt: z.string(),
});
export const omsSiteRowsSchema = z.array(omsSiteRowSchema);
/** The most domains one request may ask about: OMS refuses a longer list. */
export const OMS_MAX_DOMAINS = 100;
export const omsListingFilterSchema = z.object({
    /** Comma separated, as Off-page stores them: `medium.com`, never `www.medium.com`. */
    domains: z
        .string()
        .min(1)
        .transform((value) => value
        .split(",")
        .map((domain) => domain.trim().toLowerCase())
        .filter((domain) => domain !== ""))
        .pipe(z.array(z.string().min(1)).min(1).max(OMS_MAX_DOMAINS)),
});
export const omsListingSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("listed"),
        niche: z.string().nullable(),
        domainAuthority: z.number().nullable(),
        pageAuthority: z.number().nullable(),
        domainRating: z.number().nullable(),
        spamScore: z.number().nullable(),
        /** The cheapest real offer, in US dollars. Null when OMS has no price for it. */
        priceUsd: z.number().nullable(),
        /** Semrush's monthly visits estimate, through OMS: theirs, not measured by us. */
        traffic: z.number().nullable(),
        organicTraffic: z.number().nullable(),
    }),
    z.object({ kind: z.literal("not_listed") }),
]);
/**
 * `off` when this deployment has no OMS key. The screen then leaves the
 * columns out rather than filling them with "not listed" for every site.
 */
export const omsListingsSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("off") }),
    z.object({
        state: z.literal("on"),
        sites: z.array(z.object({ domain: z.string(), listing: omsListingSchema })),
    }),
]);
//# sourceMappingURL=oms.js.map